import { type DeviceNode, deviceNodeBox, deviceView, type TemplateNode } from "../builder";
import type { LibraryTemplate } from "../library";
import {
  device,
  during,
  ellipse,
  enter,
  logo,
  pill,
  rect,
  stack,
  TEXT_SHADOW,
  t,
  text,
  WHITE,
} from "./shorthands";

/**
 * Templates with device mockups (docs/19-mockups.md): App-Store screenshots,
 * app and website launches, a presentation slide and two videos. The screens
 * stay empty; a screenshot dropped onto a device fills its screen.
 */

export const PHONE = "iphone-17-pro";
export const LAPTOP = "macbook-pro-14";

const FRONT = deviceView(PHONE, "front");
const HERO = deviceView(PHONE, "hero");
const FLOAT = deviceView(PHONE, "float");
const LAPTOP_LEFT = deviceView(LAPTOP, "left");
const BROWSER_LEFT = deviceView("browser", "left");

/** Deep, soft shadow under devices (follows their outline). */
export const DEVICE_SHADOW = { color: "#00000052", y: 34, blur: 80 };

export type DeviceSpec = Pick<DeviceNode, "device" | "landscape" | "tilt" | "url">;
export const widthAt = (spec: DeviceSpec, h: number) => deviceNodeBox({ ...spec, x: 0, y: 0, h }).w;
export const heightAt = (spec: DeviceSpec, w: number) =>
  deviceNodeBox({ ...spec, x: 0, y: 0, w }).h;

const phoneFront = { device: PHONE, tilt: FRONT };
const phoneHero = { device: PHONE, tilt: HERO };
const phoneFloat = { device: PHONE, tilt: FLOAT };

/** Store badge (black, two lines) as in the stores' own artwork, without their logos. */
export function storeBadge(x: number, y: number, store: "apple" | "google"): TemplateNode[] {
  const w = 260;
  const h = 86;
  return [
    rect({
      x,
      y,
      w,
      h,
      fill: "#000000",
      radius: 18,
      stroke: { color: "#ffffff40", width: 2 },
      name: store === "apple" ? "App Store" : "Google Play",
    }),
    text({
      text: store === "apple" ? "Laden im" : "Jetzt bei",
      style: "caption",
      size: 19,
      color: "#ffffffd9",
      x: x + 28,
      y: y + 12,
      w: w - 56,
      shrink: true,
    }),
    text({
      text: store === "apple" ? "App Store" : "Google Play",
      style: "heading-2",
      size: 34,
      color: WHITE,
      x: x + 28,
      y: y + 34,
      w: w - 56,
      shrink: true,
    }),
  ];
}

/** One App-Store screenshot page (1080 × 2346 reference px): headline and a large phone. */
function storePage(
  name: string,
  background: string,
  headline: string,
  subline: string,
  devices: TemplateNode[],
  decoration: TemplateNode[],
): LibraryTemplate["pages"][number] {
  return {
    name,
    background,
    nodes: [
      ...decoration,
      stack({
        x: 80,
        y: 190,
        w: 920,
        align: "center",
        gap: 28,
        items: [
          t(headline, "heading-1", 112, `on:${background}`),
          t(subline, "body", 46, `on:${background}/85`),
        ],
      }),
      ...devices,
    ],
  };
}

const APP_STORE_PHONE_W = 800;
const APP_STORE_PHONE_Y = 2346 - heightAt(phoneFront, APP_STORE_PHONE_W) - 120;

export const MOCKUP_TEMPLATES: LibraryTemplate[] = [
  {
    id: "app-store-screenshots",
    title: "App-Store-Screenshots",
    description:
      "Drei Screenshots fürs iPhone (6,9″) mit Schlagzeile und Gerät – Screenshot aufs Handy ziehen",
    presetId: "app-store",
    topic: "produkt",
    style: "klar",
    tags: ["mockup", "app", "app store", "screenshot", "iphone", "google play", "store", "handy"],
    pages: [
      storePage(
        "Screenshot 1",
        "primary",
        "Alles im Blick",
        "Aufgaben und Termine an einem Ort",
        [
          device({
            ...phoneFront,
            x: (1080 - APP_STORE_PHONE_W) / 2,
            y: APP_STORE_PHONE_Y,
            w: APP_STORE_PHONE_W,
            shadow: DEVICE_SHADOW,
            name: "iPhone",
          }),
        ],
        [
          ellipse({ x: 520, y: -260, w: 820, h: 820, fill: "on:primary/10", name: "Kreis" }),
          ellipse({ x: -300, y: 1500, w: 900, h: 900, fill: "on:primary/10", name: "Kreis" }),
        ],
      ),
      storePage(
        "Screenshot 2",
        "secondary",
        "Plane in Sekunden",
        "Ein Tipp – und dein Termin steht",
        [
          device({
            ...phoneHero,
            x: (1080 - widthAt(phoneHero, 1640)) / 2,
            y: 2346 - 1640 - 110,
            h: 1640,
            shadow: DEVICE_SHADOW,
            name: "iPhone",
          }),
        ],
        [
          ellipse({ x: 380, y: 760, w: 1100, h: 1100, fill: "primary/35", name: "Kreis" }),
          ellipse({ x: -240, y: -200, w: 620, h: 620, fill: "primary/30", name: "Kreis" }),
        ],
      ),
      storePage(
        "Screenshot 3",
        "primary",
        "Gemeinsam schneller",
        "Teile Listen mit deinem Team",
        [
          device({
            ...phoneFloat,
            x: -30,
            y: 700,
            h: 1320,
            shadow: DEVICE_SHADOW,
            name: "iPhone hinten",
          }),
          device({
            ...phoneFloat,
            finish: "tiefblau",
            x: 1110 - widthAt(phoneFloat, 1320),
            y: 900,
            h: 1320,
            shadow: DEVICE_SHADOW,
            name: "iPhone vorne",
          }),
        ],
        [ellipse({ x: 200, y: 1100, w: 1200, h: 1200, fill: "on:primary/10", name: "Kreis" })],
      ),
    ],
  },
  {
    id: "post-neue-app",
    title: "Neue App",
    description: "Instagram-Post zum App-Launch: Handy schräg, Schlagzeile und Store-Badges",
    presetId: "instagram-portrait",
    topic: "ankuendigung",
    style: "verspielt",
    tags: ["mockup", "app", "launch", "screenshot", "iphone", "app store", "google play", "handy"],
    pages: [
      {
        background: "surface",
        nodes: [
          ellipse({ x: 470, y: 470, w: 920, h: 920, fill: "primary", name: "Kreis" }),
          ellipse({ x: 880, y: 150, w: 150, h: 150, fill: "accent", name: "Kreis klein" }),
          device({
            ...phoneFloat,
            x: 555,
            y: 300,
            h: 960,
            shadow: DEVICE_SHADOW,
            name: "iPhone",
          }),
          logo({ x: 80, y: 80, w: 240, h: 72, on: "surface" }),
          stack({
            x: 80,
            y: 240,
            w: 450,
            gap: [22, 28],
            items: [
              t("NEU", "subline", 30, "primary"),
              t("Unsere App ist da", "heading-1", 88, "text"),
              t("Termine buchen, Erinnerungen bekommen – in Sekunden.", "body", 34, "text-muted"),
            ],
          }),
          ...storeBadge(80, 1060, "apple"),
          ...storeBadge(80, 1166, "google"),
        ],
      },
    ],
  },
  {
    id: "story-app-feature",
    title: "Story: Neue Funktion",
    description: "Story mit großem Handy in Perspektive und Schlagzeile – für App-Updates",
    presetId: "story",
    topic: "produkt",
    style: "nacht",
    tags: ["mockup", "app", "update", "feature", "screenshot", "iphone", "handy", "story"],
    pages: [
      {
        background: "background",
        nodes: [
          ellipse({ x: -260, y: 600, w: 1000, h: 1000, fill: "primary/25", name: "Kreis" }),
          ellipse({ x: 560, y: 1180, w: 760, h: 760, fill: "accent/20", name: "Kreis" }),
          logo({ x: 340, y: 150, w: 400, h: 80, on: "background", align: "center" }),
          stack({
            x: 90,
            y: 290,
            w: 900,
            align: "center",
            gap: 20,
            items: [
              t("NEUE FUNKTION", "subline", 38, "accent"),
              t("Jetzt mit Kalender-Sync", "heading-1", 104, "text"),
            ],
          }),
          device({
            ...phoneHero,
            x: (1080 - widthAt(phoneHero, 1100)) / 2,
            y: 640,
            h: 1100,
            shadow: { color: "#00000080", y: 40, blur: 90 },
            glare: true,
            name: "iPhone",
          }),
        ],
      },
    ],
  },
  {
    id: "post-app-screens",
    title: "App-Screens im Überblick",
    description: "Quadratischer Post mit drei gefächerten Handys für drei Screenshots",
    presetId: "square",
    topic: "produkt",
    style: "warm",
    tags: ["mockup", "app", "screenshots", "iphone", "handy", "galerie", "vorschau"],
    pages: [
      {
        background: "primary",
        nodes: [
          ellipse({ x: 190, y: 380, w: 700, h: 700, fill: "on:primary/15", name: "Kreis" }),
          stack({
            x: 80,
            y: 80,
            w: 920,
            align: "center",
            gap: 12,
            items: [
              t("So sieht’s aus", "heading-1", 84, "on:primary"),
              t("Drei Blicke in unsere neue App", "body", 34, "on:primary/85"),
            ],
          }),
          ...(
            [
              { x: 120, rotation: -10 },
              { x: 960 - widthAt(phoneFront, 600), rotation: 10 },
            ] as const
          ).map((side) =>
            device({
              ...phoneFront,
              x: side.x,
              y: 400,
              h: 600,
              rotation: side.rotation,
              shadow: DEVICE_SHADOW,
              name: "iPhone",
            }),
          ),
          device({
            ...phoneFront,
            x: (1080 - widthAt(phoneFront, 720)) / 2,
            y: 300,
            h: 720,
            shadow: DEVICE_SHADOW,
            name: "iPhone Mitte",
          }),
        ],
      },
    ],
  },
  {
    id: "linkedin-website-launch",
    title: "LinkedIn: Website-Launch",
    description: "Neue Website im Browserfenster und auf dem Handy, Schlagzeile und Adresse",
    presetId: "linkedin-landscape",
    topic: "ankuendigung",
    style: "klar",
    tags: [
      "mockup",
      "website",
      "webseite",
      "relaunch",
      "browser",
      "launch",
      "screenshot",
      "online",
    ],
    pages: [
      {
        background: "secondary",
        nodes: [
          ellipse({ x: 560, y: -120, w: 700, h: 700, fill: "primary/40", name: "Kreis" }),
          logo({ x: 60, y: 44, w: 200, h: 52, on: "secondary" }),
          stack({
            x: 60,
            y: 290,
            w: 450,
            anchor: "center",
            gap: [16, 20],
            items: [
              t("RELAUNCH", "subline", 22, "accent"),
              t("Unsere neue Website ist online", "heading-1", 52, "on:secondary"),
              t("Schneller, übersichtlicher und endlich mobil.", "body", 22, "on:secondary/80"),
            ],
          }),
          ...pill(60, 468, 280, 54, "www.beispiel.de", "accent", 22),
          device({
            device: "browser",
            tilt: BROWSER_LEFT,
            url: "www.beispiel.de",
            x: 530,
            y: 70,
            w: 500,
            shadow: DEVICE_SHADOW,
            name: "Browser",
          }),
          device({
            ...phoneFront,
            x: 1040 - widthAt(phoneFront, 300),
            y: 230,
            h: 300,
            shadow: DEVICE_SHADOW,
            name: "iPhone",
          }),
        ],
      },
    ],
  },
  {
    id: "praesentation-produkt",
    title: "Präsentation: Produkt mit Laptop",
    description:
      "Folie im Querformat 16:9 mit Laptop, Schlagzeile und Button – auch für Product Hunt",
    presetId: "presentation",
    topic: "produkt",
    style: "klar",
    tags: [
      "mockup",
      "präsentation",
      "folie",
      "laptop",
      "macbook",
      "software",
      "product hunt",
      "website",
    ],
    pages: [
      {
        background: "background",
        nodes: [
          ellipse({ x: 560, y: -160, w: 760, h: 900, fill: "surface", name: "Kreis" }),
          logo({ x: 60, y: 44, w: 170, h: 46, on: "background" }),
          stack({
            x: 60,
            y: 310,
            w: 440,
            anchor: "center",
            gap: [14, 18],
            items: [
              t("PRODUKT-UPDATE", "subline", 18, "primary"),
              t("Die neue Version ist da", "heading-1", 50, "text"),
              t(
                "Schneller, klarer und mit Team-Funktionen – jetzt 30 Tage kostenlos testen.",
                "body",
                19,
                "text-muted",
              ),
            ],
          }),
          ...pill(60, 470, 210, 54, "Jetzt testen", "primary", 20),
          device({
            device: LAPTOP,
            tilt: LAPTOP_LEFT,
            x: 470,
            y: 90,
            w: 580,
            shadow: DEVICE_SHADOW,
            name: "MacBook",
          }),
        ],
      },
    ],
  },
  {
    id: "reel-app-vorstellen",
    title: "Reel: App vorstellen",
    description:
      "Das Handy fliegt ein, drei Funktionen erscheinen nacheinander, zum Schluss der Aufruf",
    presetId: "reel",
    topic: "produkt",
    style: "verspielt",
    previewTime: 2500,
    tags: ["mockup", "app", "screenshot", "iphone", "handy", "funktionen", "launch", "animation"],
    pages: [
      {
        background: "secondary",
        nodes: [
          ellipse({
            x: -260,
            y: -300,
            w: 760,
            h: 760,
            fill: "primary/40",
            name: "Kreis",
            ...enter("fade", { duration: 800 }),
          }),
          ellipse({
            x: 820,
            y: 150,
            w: 360,
            h: 360,
            fill: "accent/25",
            name: "Kreis",
            ...enter("fade", { duration: 1200 }),
          }),
          device({
            ...phoneFront,
            x: (1080 - widthAt(phoneFront, 900)) / 2,
            y: 530,
            h: 900,
            shadow: { color: "#00000080", y: 40, blur: 90 },
            name: "iPhone",
            ...enter("fly-up", { duration: 900 }),
          }),
          ...[
            { n: "01", text: "Termine in Sekunden buchen", start: 1000, end: 4500 },
            { n: "02", text: "Erinnerungen, die helfen", start: 4500, end: 8000 },
            { n: "03", text: "Mit deinem Team teilen", start: 8000, end: 11500 },
          ].map((feature) =>
            stack({
              x: 90,
              y: 250,
              w: 900,
              align: "center",
              gap: 10,
              items: [
                t(feature.n, "subline", 40, "accent", during(feature.start, feature.end, "fade")),
                t(feature.text, "heading-1", 76, WHITE, {
                  shadow: TEXT_SHADOW,
                  ...during(feature.start, feature.end, "slide-up"),
                }),
              ],
            }),
          ),
          stack({
            x: 90,
            y: 250,
            w: 900,
            align: "center",
            gap: 26,
            items: [
              t("Jetzt kostenlos laden", "heading-1", 84, WHITE, {
                shadow: TEXT_SHADOW,
                ...during(11500, 15000, "pop", { out: null }),
              }),
            ],
          }),
          ...pill(290, 400, 500, 90, "Link in der Bio", "accent", 36, {
            ...during(11800, 15000, "slide-up", { out: null }),
          }),
        ],
      },
    ],
  },
  {
    id: "video-software-demo",
    title: "Video: Software-Demo mit Laptop",
    description: "Querformat-Video mit Laptop, Titel und drei Kapiteln nacheinander",
    presetId: "video-landscape",
    topic: "produkt",
    style: "klar",
    previewTime: 3500,
    tags: ["mockup", "software", "demo", "laptop", "macbook", "website", "tutorial", "youtube"],
    pages: [
      {
        background: "surface",
        nodes: [
          ellipse({
            x: 40,
            y: 520,
            w: 520,
            h: 520,
            fill: "primary/15",
            name: "Kreis",
            ...enter("fade", { duration: 800 }),
          }),
          logo({ x: 120, y: 80, w: 260, h: 70, on: "surface" }),
          stack({
            x: 120,
            y: 470,
            w: 700,
            anchor: "center",
            gap: 18,
            items: [
              t("SOFTWARE-DEMO", "subline", 34, "primary", enter("fade", { duration: 600 })),
              t(
                "So funktioniert unser Dashboard",
                "heading-1",
                88,
                "text",
                enter("slide-up", { duration: 700 }),
              ),
            ],
          }),
          ...[
            { label: "1 · Daten importieren", start: 2500, end: 6000 },
            { label: "2 · Berichte erstellen", start: 6000, end: 9500 },
            { label: "3 · Mit dem Team teilen", start: 9500, end: 13000 },
          ].flatMap((chapter) =>
            pill(120, 760, 560, 96, chapter.label, "primary", 38, {
              ...during(chapter.start, chapter.end, "slide-right"),
            }),
          ),
          device({
            device: LAPTOP,
            tilt: LAPTOP_LEFT,
            x: 860,
            y: 1080 - heightAt({ device: LAPTOP, tilt: LAPTOP_LEFT }, 980) - 120,
            w: 980,
            shadow: DEVICE_SHADOW,
            name: "MacBook",
            ...enter("zoom", { duration: 900 }),
          }),
        ],
      },
    ],
  },
];
